import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Clock, 
  CheckCircle, 
  UserCheck,
  Calendar,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import {
  KPICard,
  ChartContainer,
  CustomLineChart,
  CustomAreaChart,
  CustomBarChart,
  CustomPieChart,
  MultiLineChart,
  ChartLoading,
  ChartNoData
} from './charts/ChartComponents.tsx';
import {
  FinancialMetrics,
  OperationalMetrics,
  ClientAnalytics,
  EmployeeAnalytics,
  ContractorAnalytics,
  KPIMetric,
  PredictiveAnalytics,
  ReportFilter
} from '../types/analytics';
import { AnalyticsCalculations } from '../utils/analyticsCalculations.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { Employee } from '../types/employee';
import { TimesheetWeek } from '../types/timesheet';
import { Invoice } from '../types/invoice';
import { Contractor } from '../types/contractor';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';

const ExecutiveDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  
  // Analytics Data State
  const [kpiMetrics, setKpiMetrics] = useState<KPIMetric[]>([]);
  const [financialMetrics, setFinancialMetrics] = useState<FinancialMetrics | null>(null);
  const [operationalMetrics, setOperationalMetrics] = useState<OperationalMetrics | null>(null);
  const [clientAnalytics, setClientAnalytics] = useState<ClientAnalytics[]>([]);
  const [employeeAnalytics, setEmployeeAnalytics] = useState<EmployeeAnalytics[]>([]);
  const [contractorAnalytics, setContractorAnalytics] = useState<ContractorAnalytics[]>([]);
  const [predictiveAnalytics, setPredictiveAnalytics] = useState<PredictiveAnalytics | null>(null);
  
  // Filter State
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: format(startOfMonth(subDays(new Date(), 30)), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  
  const [refreshInterval, setRefreshInterval] = useState<number>(300000); // 5 minutes
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Load analytics data
  const loadAnalyticsData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch data from Firebase
      const [employeesSnapshot, timesheetsSnapshot, invoicesSnapshot, contractorsSnapshot] = await Promise.all([
        getDocs(collection(db, 'employees')),
        getDocs(query(collection(db, 'timesheets'), orderBy('weekEnding', 'desc'), limit(100))),
        getDocs(query(collection(db, 'invoices'), orderBy('invoiceDate', 'desc'), limit(100))),
        getDocs(collection(db, 'contractors'))
      ]);

      // Convert to typed arrays
      const employees: Employee[] = employeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        hireDate: doc.data().hireDate?.toDate() || new Date(),
        idExpires: doc.data().idExpires?.toDate(),
        tdlrExpires: doc.data().tdlrExpires?.toDate(),
        scissorLiftTraining: doc.data().scissorLiftTraining?.toDate(),
        firstAidCpr: doc.data().firstAidCpr?.toDate()
      })) as Employee[];

      const timesheets: TimesheetWeek[] = timesheetsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        weekStarting: doc.data().weekStarting?.toDate() || new Date(),
        weekEnding: doc.data().weekEnding?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        submittedAt: doc.data().submittedAt?.toDate(),
        approvedAt: doc.data().approvedAt?.toDate()
      })) as TimesheetWeek[];

      const invoices: Invoice[] = invoicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        invoiceDate: doc.data().invoiceDate?.toDate() || new Date(),
        dueDate: doc.data().dueDate?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Invoice[];

      const contractors: Contractor[] = contractorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        lastLoginAt: doc.data().lastLoginAt?.toDate(),
        licenseExpires: doc.data().licenseExpires?.toDate(),
        insuranceExpires: doc.data().insuranceExpires?.toDate(),
        bondExpires: doc.data().bondExpires?.toDate()
      })) as Contractor[];

      // Calculate analytics
      const financial = AnalyticsCalculations.calculateFinancialMetrics(invoices, dateRange);
      const operational = AnalyticsCalculations.calculateOperationalMetrics(employees, timesheets, contractors, dateRange);
      const clientData = AnalyticsCalculations.calculateClientAnalytics(invoices, timesheets, dateRange);
      const employeeData = AnalyticsCalculations.calculateEmployeeAnalytics(employees, timesheets, dateRange);
      const contractorData = AnalyticsCalculations.calculateContractorAnalytics(contractors, dateRange);
      const kpis = AnalyticsCalculations.generateKPIMetrics(financial, operational);
      const predictive = AnalyticsCalculations.generatePredictiveAnalytics(financial, operational);

      // Update state
      setFinancialMetrics(financial);
      setOperationalMetrics(operational);
      setClientAnalytics(clientData);
      setEmployeeAnalytics(employeeData);
      setContractorAnalytics(contractorData);
      setKpiMetrics(kpis);
      setPredictiveAnalytics(predictive);
      setLastUpdated(new Date());

    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load and auto-refresh setup
  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadAnalyticsData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  // Format KPI values
  const formatKPIValue = (metric: KPIMetric): string => {
    switch (metric.format) {
      case 'currency':
        return AnalyticsCalculations.formatCurrency(metric.value as number);
      case 'percentage':
        return AnalyticsCalculations.formatPercentage(metric.value as number);
      case 'hours':
        return AnalyticsCalculations.formatHours(metric.value as number);
      case 'number':
        return AnalyticsCalculations.formatNumber(metric.value as number);
      default:
        return metric.value.toString();
    }
  };

  // Get icon component
  const getIcon = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      DollarSign: <DollarSign className="h-8 w-8" />,
      TrendingUp: <TrendingUp className="h-8 w-8" />,
      Clock: <Clock className="h-8 w-8" />,
      Users: <Users className="h-8 w-8" />,
      UserCheck: <UserCheck className="h-8 w-8" />,
      CheckCircle: <CheckCircle className="h-8 w-8" />
    };
    return iconMap[iconName] || <TrendingUp className="h-8 w-8" />;
  };

  // Prepare chart data
  const revenueChartData = financialMetrics?.monthlyRevenue.map(item => ({
    month: format(new Date(item.date), 'MMM'),
    revenue: item.value,
    expenses: financialMetrics.monthlyExpenses.find(exp => exp.date === item.date)?.value || 0
  })) || [];

  const clientRevenueData = Object.entries(financialMetrics?.revenueByClient || {}).map(([name, value]) => ({
    name,
    value
  }));

  const expenseBreakdownData = Object.entries(financialMetrics?.expensesByCategory || {}).map(([name, value]) => ({
    name,
    value
  }));

  const topEmployeesData = employeeAnalytics.slice(0, 10).map(emp => ({
    name: emp.employeeName.split(' ').map(n => n[0]).join(''),
    revenue: emp.totalRevenue,
    hours: emp.totalHours
  }));

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadAnalyticsData}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Executive Dashboard</h1>
              <p className="text-sm text-gray-500">
                Last updated: {format(lastUpdated, 'MMM dd, yyyy HH:mm')}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Date Range Filter */}
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                />
              </div>
              
              {/* Refresh Button */}
              <button
                onClick={loadAnalyticsData}
                disabled={isLoading}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow-md animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {kpiMetrics.map((metric) => (
                <KPICard
                  key={metric.id}
                  title={metric.title}
                  value={formatKPIValue(metric)}
                  change={metric.change}
                  changeType={metric.changeType}
                  icon={getIcon(metric.icon || 'TrendingUp')}
                  color={metric.color}
                />
              ))}
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Revenue Trend */}
              <ChartContainer title="Revenue & Expenses Trend">
                {revenueChartData.length > 0 ? (
                  <MultiLineChart
                    data={revenueChartData}
                    xKey="month"
                    lines={[
                      { key: 'revenue', name: 'Revenue', color: '#10B981' },
                      { key: 'expenses', name: 'Expenses', color: '#EF4444' }
                    ]}
                    height={300}
                  />
                ) : (
                  <ChartNoData message="No revenue data available for selected period" />
                )}
              </ChartContainer>

              {/* Client Revenue Distribution */}
              <ChartContainer title="Revenue by Client">
                {clientRevenueData.length > 0 ? (
                  <CustomPieChart
                    data={clientRevenueData}
                    dataKey="value"
                    nameKey="name"
                    height={300}
                  />
                ) : (
                  <ChartNoData message="No client revenue data available" />
                )}
              </ChartContainer>

              {/* Top Employees by Revenue */}
              <ChartContainer title="Top Employees by Revenue">
                {topEmployeesData.length > 0 ? (
                  <CustomBarChart
                    data={topEmployeesData}
                    xKey="name"
                    yKey="revenue"
                    barColor="#3B82F6"
                    height={300}
                  />
                ) : (
                  <ChartNoData message="No employee data available" />
                )}
              </ChartContainer>

              {/* Expense Breakdown */}
              <ChartContainer title="Expense Breakdown">
                {expenseBreakdownData.length > 0 ? (
                  <CustomPieChart
                    data={expenseBreakdownData}
                    dataKey="value"
                    nameKey="name"
                    colors={['#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4']}
                    height={300}
                  />
                ) : (
                  <ChartNoData message="No expense data available" />
                )}
              </ChartContainer>
            </div>

            {/* Predictive Analytics */}
            {predictiveAnalytics && (
              <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-8">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Predictive Analytics</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Revenue Forecasting</h4>
                      <div className="space-y-1 text-sm">
                        <p>Next Month: {AnalyticsCalculations.formatCurrency(predictiveAnalytics.revenueForecasting.nextMonth)}</p>
                        <p>Next Quarter: {AnalyticsCalculations.formatCurrency(predictiveAnalytics.revenueForecasting.nextQuarter)}</p>
                        <p>Next Year: {AnalyticsCalculations.formatCurrency(predictiveAnalytics.revenueForecasting.nextYear)}</p>
                        <p className="text-blue-700">Trend: {predictiveAnalytics.revenueForecasting.trend}</p>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-2">Resource Planning</h4>
                      <div className="space-y-1 text-sm">
                        <p>Recommended Employees: {predictiveAnalytics.resourcePlanning.recommendedEmployees}</p>
                        <p>Recommended Contractors: {predictiveAnalytics.resourcePlanning.recommendedContractors}</p>
                        <p>Peak Periods: {predictiveAnalytics.resourcePlanning.peakDemandPeriods.join(', ')}</p>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-900 mb-2">Risk Assessment</h4>
                      <div className="space-y-1 text-sm">
                        <p>Overall Risk: {predictiveAnalytics.riskAssessment.overallRisk}%</p>
                        <div className="mt-2">
                          <p className="font-medium">Recommendations:</p>
                          <ul className="list-disc list-inside text-xs space-y-1">
                            {predictiveAnalytics.riskAssessment.recommendations.map((rec, index) => (
                              <li key={index}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Clients */}
              <div className="bg-white rounded-lg shadow-md border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Top Clients</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {clientAnalytics.slice(0, 5).map((client, index) => (
                      <div key={client.clientName} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-800">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{client.clientName}</p>
                            <p className="text-sm text-gray-500">{client.totalHours.toFixed(1)} hours</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">
                            {AnalyticsCalculations.formatCurrency(client.totalRevenue)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {AnalyticsCalculations.formatCurrency(client.averageHourlyRate)}/hr
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Employees */}
              <div className="bg-white rounded-lg shadow-md border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Top Employees</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {employeeAnalytics.slice(0, 5).map((employee, index) => (
                      <div key={employee.employeeId} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-sm font-medium text-green-800">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{employee.employeeName}</p>
                            <p className="text-sm text-gray-500">{employee.totalHours.toFixed(1)} hours</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">
                            {AnalyticsCalculations.formatCurrency(employee.totalRevenue)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {employee.utilization.toFixed(1)}% utilization
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
